import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Notification } from '../types';
import { supabase, withRetry, withTimeout } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await withTimeout(
        withRetry(() =>
          supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
        )
      );

      if (error) throw error;

      const formattedNotifications: Notification[] = (data || []).map(notification => ({
        id: notification.id,
        userId: notification.user_id,
        title: notification.title,
        message: notification.message,
        type: notification.type,
        read: notification.read,
        link: notification.link,
        createdAt: notification.created_at
      }));

      setNotifications(formattedNotifications);

      // Show new notifications
      formattedNotifications
        .filter(n => !n.read)
        .forEach(n => {
          toast[n.type](n.message, {
            description: n.title,
            action: n.link ? {
              label: 'View',
              onClick: () => window.location.href = n.link!
            } : undefined
          });
        });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch notifications';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const notification = payload.new as any;
          const formattedNotification: Notification = {
            id: notification.id,
            userId: notification.user_id,
            title: notification.title,
            message: notification.message,
            type: notification.type,
            read: notification.read,
            link: notification.link,
            createdAt: notification.created_at
          };

          setNotifications(prev => [formattedNotification, ...prev]);

          // Show toast for new notification
          toast[formattedNotification.type](formattedNotification.message, {
            description: formattedNotification.title,
            action: formattedNotification.link ? {
              label: 'View',
              onClick: () => window.location.href = formattedNotification.link!
            } : undefined
          });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [user]);

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    try {
      setLoading(true);

      const { error } = await withTimeout(
        withRetry(() =>
          supabase
            .from('notifications')
            .update({ read: true })
            .eq('id', notificationId)
            .eq('user_id', user.id)
        )
      );

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to mark notification as read';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { error } = await withTimeout(
        withRetry(() =>
          supabase
            .from('notifications')
            .update({ read: true })
            .eq('user_id', user.id)
            .eq('read', false)
        )
      );

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );

      toast.success('All notifications marked as read');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to mark all notifications as read';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user) return;

    try {
      setLoading(true);

      const { error } = await withTimeout(
        withRetry(() =>
          supabase
            .from('notifications')
            .delete()
            .eq('id', notificationId)
            .eq('user_id', user.id)
        )
      );

      if (error) throw error;

      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );

      toast.success('Notification deleted');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete notification';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const clearAllNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { error } = await withTimeout(
        withRetry(() =>
          supabase
            .from('notifications')
            .delete()
            .eq('user_id', user.id)
        )
      );

      if (error) throw error;

      setNotifications([]);
      toast.success('All notifications cleared');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to clear notifications';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        refreshNotifications: fetchNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};